import React, { useEffect, useState, useContext, useMemo } from 'react';
import { useLocation } from 'react-router-dom';
import { useFormContext } from 'react-hook-form';
import Joyride from 'react-joyride';
import { useDispatch, useSelector } from 'react-redux';
import { ClaimDetails } from '../../components/claims/claimDetails/claimDetails';
import OnboardingTooltip from '../../components/onboarding/onboardingTooltip';
import { PetDetailsRadios } from '../../components/petDetailRadios/petDetailsRadios';
import Loading from '../../components/shared/Loading/Loading';
import { SLICES } from '../../constants/slices';
import { useGetDashboardClaimsQuery, useGetSavedClaimsQuery } from '../../data/claim';
import { useCreatePortalUserTokenMutation, useGetWelcomeDashboardQuery } from '../../data/customer';
import { useGetPetInfoQuery } from '../../data/policy';
import { FeatureFlagContext } from '../../contextProviders/featureFlagContext';
import { setPetDetails } from '../../store/slice/petDetailsSlice';
import { sortClaims } from '../../utils/sort';
import { useGetMarketingTilesQuery } from '../../data/kontentAi';
import { BrandContext } from '../../contextProviders/brandContext';
import { AUTHENTICATION_TYPES, EMPTY_RECORD_MESSAGE, WELCOME_DASHBOARD } from '../../constants/constants';
import { EmptyRecord } from '../../components/emptyRecord/emptyRecord';
import './dashboard.scss';
import getSteps from './steps';
import { SESSION_STORAGE_KEYS } from '../../constants/sessionStorageKeys';
import LatestClaimsActionButtons from './latestClaimsActionButtons';
import { MREC_TILES } from '../../constants/cmsCodeName';
import { getGtmService } from '../../services/gtmService/GtmService';
import { TermsOfUsePopup } from './termsOfUsePopup';
import useDashboard from './useDashboard';
import { setTermsOfUsePopupVisible } from '../../store/slice/dashboardSlice';

export const Dashboard = () => {
  const { cspConfiguration } = useContext(FeatureFlagContext);
  const isLiveVetEnabled = cspConfiguration?.IsLiveVetEnabled || false;
  const isLiteModeEnabled = cspConfiguration?.IsLiteModeEnabled;
  const isCreatePortalUserTokenEnabled = cspConfiguration?.IsCreatePortalUserTokenEnabled || false;
  const skipQueryOnLiteMode = isLiteModeEnabled !== false;
  const steps = useMemo(() => getSteps(isLiveVetEnabled), [isLiveVetEnabled]);
  const isKontentAiPreviewEnabled = cspConfiguration?.IsKontentAIPreviewEnabled || false;
  const isV3PenTestRemediationEndpointsEnabled = cspConfiguration?.IsV3PenTestRemediationEndpointsEnabled;
  const dispatch = useDispatch();
  const [selectedPet, setSelectedPet] = useState('');
  const [filteredClaimsList, setFilteredClaimsList] = useState([]);
  const { authDetails, tokenClaims } = useSelector((state) => state?.[SLICES.AUTHENTICATION]);
  const { termsOfUsePopupVisible } = useSelector((state) => state?.[SLICES.DASHBOARD]);

  const { cspAccessToken, contactId, partnerNumber, auth0AccessToken } = authDetails;
  const {
    data: firstLoginCheck,
    isLoading: isLoadingGetWelcomeDashboardQuery,
    isFetching: isWelcomeDashboardFetching
  } = useGetWelcomeDashboardQuery(
    {
      auth0Token: auth0AccessToken,
      isFallback: cspConfiguration?.IsFallbackModeMiddlewareApiEnabled,
      contactId
    },
    { skip: !auth0AccessToken || !cspConfiguration || !contactId || skipQueryOnLiteMode }
  );
  const [createPortalUserToken] = useCreatePortalUserTokenMutation();
  const [hasCreatedPortalUserToken, setHasCreatedPortalUserToken] = useState(false);

  // Using the RTK query hook for getting pet info (only triggered when token is available)
  const {
    data: petDetails,
    isLoading: petDetailsLoading,

    refetch: getPetInfo
  } = useGetPetInfoQuery(auth0AccessToken, { skip: !auth0AccessToken || skipQueryOnLiteMode });

  const { data: dashboardClaims, isLoading: dashboardClaimsLoading } = useGetDashboardClaimsQuery(auth0AccessToken, {
    skip: !cspAccessToken || !auth0AccessToken
  });

  const { data: savedClaims, isLoading: savedClaimsLoading } = useGetSavedClaimsQuery(
    {
      contactId,
      partnerNumber,
      auth0Token: auth0AccessToken,
      isV3ApiEnabled: isV3PenTestRemediationEndpointsEnabled
    },
    {
      skip:
        petDetailsLoading ||
        !cspAccessToken ||
        !contactId ||
        !partnerNumber ||
        skipQueryOnLiteMode ||
        isV3PenTestRemediationEndpointsEnabled === undefined ||
        (isV3PenTestRemediationEndpointsEnabled && !auth0AccessToken)
    }
  );
  const { activeBrandContext } = useContext(BrandContext);
  const { data: dashboardTiles } = useGetMarketingTilesQuery({
    brandPartner: activeBrandContext.brand.key,
    mrecTile: MREC_TILES.DASHBOARD,
    previewEnabled: isKontentAiPreviewEnabled
  });
  const { authenticationType } =
    useContext(BrandContext).activeBrandContext.cspConfiguration.auth0Config[process.env.REACT_APP_ENV];
  const isSSOAuthentication = authenticationType === AUTHENTICATION_TYPES.SSO;

  const [isWelcomeDashboard, setIsWelcomeDashboard] = useState(false);
  const { handleUpdateFirstLoginExperience } = useDashboard({
    featureFlags: cspConfiguration,
    setIsWelcomeDashboard,
    auth0AccessToken,
    contactId,
    isSSOAuthentication
  });

  const [showTour, setShowTour] = useState(false);
  const [takeATourStatus, setTakeATourStatus] = useState(false);
  const [showTermsOfUse, setShowTermsOfUse] = useState(false);
  const [termsOfUseStatus, setTermsOfUseStatus] = useState(
    (firstLoginCheck?.TermsOfUseStatus == null && isSSOAuthentication) || null
  );
  const location = useLocation();
  useEffect(() => {
    if (!isLoadingGetWelcomeDashboardQuery && firstLoginCheck !== undefined) {
      const showTermsOfUseFlag = firstLoginCheck?.TermsOfUseStatus == null && isSSOAuthentication;
      const isWelcomeDashboardFlag = isSSOAuthentication
        ? firstLoginCheck?.TermsOfUseStatus == null && firstLoginCheck?.TakeATourStatus == null
        : firstLoginCheck?.TakeATourStatus == null;
      setShowTermsOfUse(showTermsOfUseFlag);
      setIsWelcomeDashboard(isWelcomeDashboardFlag);
      if (isWelcomeDashboardFlag) {
        sessionStorage.setItem(SESSION_STORAGE_KEYS.IS_FIRST_LOGIN, 'true');
      }
    }
  }, [isLoadingGetWelcomeDashboardQuery, firstLoginCheck, isSSOAuthentication]);

  useEffect(() => {
    const showOnBoardingTour = sessionStorage.getItem(SESSION_STORAGE_KEYS.SHOW_ONBOARDING) === 'true';
    setShowTour(showOnBoardingTour);
  }, [location]);

  useEffect(() => {
    if (skipQueryOnLiteMode) return;
    if (!petDetails) {
      try {
        getPetInfo();
      } catch (e) {
        throw Error('Error getting pet info: ', e);
      }
    } else {
      dispatch(setPetDetails(petDetails));
    }
  }, [petDetails, getPetInfo, dispatch, skipQueryOnLiteMode]);

  useEffect(() => {
    if (dashboardClaims) {
      setFilteredClaimsList(dashboardClaims);
    }
  }, [dashboardClaims]);

  useEffect(() => {
    if (skipQueryOnLiteMode) return;
    // Only call createPortalUserToken after isWelcomeDashboard has been fetched
    if (
      isCreatePortalUserTokenEnabled &&
      !isWelcomeDashboardFetching &&
      isWelcomeDashboard !== undefined &&
      !tokenClaims?.registrationInProgress &&
      !!auth0AccessToken &&
      !!cspAccessToken &&
      !hasCreatedPortalUserToken
    ) {
      createPortalUserToken({
        auth0Token: auth0AccessToken,
        customerToken: cspAccessToken
      });
      setHasCreatedPortalUserToken(true);
    }
  }, [
    isWelcomeDashboardFetching,
    isWelcomeDashboard,
    tokenClaims?.registrationInProgress,
    cspAccessToken,
    auth0AccessToken,
    hasCreatedPortalUserToken,
    createPortalUserToken,
    skipQueryOnLiteMode,
    isCreatePortalUserTokenEnabled
  ]);

  const { resetField } = useFormContext();

  const handlePetSelection = (fullPolicyNumber) => {
    // TEMPORARY - Data to be managed via reducer
    const petClaims = dashboardClaims.filter((claim) => fullPolicyNumber.indexOf(claim.PolicyNumber) !== -1);
    setFilteredClaimsList(petClaims);
    setSelectedPet(fullPolicyNumber);
  };

  const clearPetSelection = () => {
    setFilteredClaimsList(dashboardClaims);
    setSelectedPet('');
    resetField('petDetailsRadios');
  };

  const handleTourCallback = (data) => {
    const { status, action, type, index, step } = data;

    // Joyride is the single source of truth for tour analytics. 'tour:start' fires once when a
    // tour actually begins; 'tooltip' fires once when each step becomes visible. tour_started
    // was previously pushed from three separate call sites — one of them an effect keyed on
    // `location`, which re-fired it on every route change — so the started/step funnel had a
    // denominator larger than its numerator. Emitting both from here keeps them consistent.
    if (type === 'tour:start') {
      getGtmService().pushTourStarted();
    }

    // The old DOM-scraping tour_step variable broke under React because no push existed.
    if (type === 'tooltip') {
      getGtmService().pushTourStep(index, step?.target);
    }

    if (status === 'skipped' || action === 'skip' || action === 'close') {
      const takeATourValue =
        action === 'skip' ? WELCOME_DASHBOARD.TAKEATOUR.SKIPPED : WELCOME_DASHBOARD.TAKEATOUR.COMPLETED;
      setShowTour(false);
      sessionStorage.setItem(SESSION_STORAGE_KEYS.SHOW_ONBOARDING, 'false');
      setTakeATourStatus(takeATourValue);
      if (isWelcomeDashboard) {
        handleFirstUserLoginCallback(takeATourValue);
      }
    }
  };

  const handleFirstUserLoginCallback = (takeATourValue) => {
    const takeATourStatusValue = takeATourValue;
    handleUpdateFirstLoginExperience(termsOfUseStatus, takeATourStatusValue);
  };

  const handleGtmClick = (promotion) => {
    const gtm = getGtmService();
    gtm.pushSelectPromotion(promotion);
  };

  const handleNextClick = () => {
    // sessionStorage.setItem(SESSION_STORAGE_KEYS.SHOW_TERMSOFUSE, 'false');
    dispatch(setTermsOfUsePopupVisible(false));
    setTermsOfUseStatus(WELCOME_DASHBOARD.TERMSOFUSE.ACCEPTED);
    setShowTermsOfUse(false); // Hide TermsOfUsePopup when Next is clicked
    setShowTour(true); // Show the tour after accepting terms; Joyride's tour:start emits tour_started
  };

  if (!cspAccessToken) {
    return <p>Please login in</p>;
  }

  // Limit to max 5 items displayed on dashboard, savedClaims first, then other claims
  const currentSavedClaims = (savedClaims && sortClaims(savedClaims)) || [];
  const currentOtherClaims = filteredClaimsList || [];
  const savedClaimsCount = Math.min(currentSavedClaims.length, 5);
  const otherClaimsCount = 5 - savedClaimsCount;

  return (
    <>
      {isWelcomeDashboard && showTermsOfUse && <TermsOfUsePopup onNext={handleNextClick} />}
      {(isWelcomeDashboard || showTour) && !showTermsOfUse && (
        <Joyride
          steps={steps}
          continuous
          tooltipComponent={OnboardingTooltip}
          scrollOffset={300}
          callback={handleTourCallback}
          styles={{
            options: {
              zIndex: 1000
            }
          }}
        />
      )}
      {!showTermsOfUse && (
        <>
          <aside
            className="pet-container"
            aria-label="Dashboard my pets section"
          >
            <div className="dashboardMyPets">
              <div className="myPetsHeader">
                <h2>My pets</h2>
              </div>
              {petDetailsLoading ? (
                <Loading />
              ) : (
                <PetDetailsRadios
                  value={selectedPet}
                  petDetails={petDetails || []}
                  onChange={handlePetSelection}
                  hasImageBackground
                />
              )}
            </div>

            {dashboardTiles?.data &&
              dashboardTiles?.data?.map((item) => {
                if (item.codename === MREC_TILES.LIVEVET && !isLiveVetEnabled) return null;
                return (
                  <div
                    className="marketingTile"
                    key={item.heading}
                  >
                    {item?.linkUrl ? (
                      <a
                        onClick={() => handleGtmClick(item.heading)}
                        href={item.linkUrl}
                      >
                        <img
                          src={item.image?.url || item.blobImage}
                          alt={item.image?.alt || item.heading}
                        />
                      </a>
                    ) : (
                      <img
                        src={item.image?.url || item.blobImage}
                        alt={item.image?.alt || item.heading}
                      />
                    )}
                  </div>
                );
              })}
          </aside>
          <main aria-label="Dashboard Latest Claims section">
            <div className="myLatestClaims">
              <div className="myLatestClaimsHeader">
                <h2>My latest claims</h2>
                <LatestClaimsActionButtons />
              </div>
              {savedClaimsLoading || petDetailsLoading || dashboardClaimsLoading ? (
                <Loading />
              ) : (
                <>
                  {currentOtherClaims.length === 0 && currentSavedClaims.length === 0 && (
                    <EmptyRecord message={EMPTY_RECORD_MESSAGE.CLAIMS} />
                  )}

                  {currentSavedClaims.slice(0, savedClaimsCount).length > 0 && (
                    <div className="myLatestClaimsList">
                      <ClaimDetails
                        icon="save"
                        petDetails={petDetails}
                        claimDetails={currentSavedClaims.slice(0, savedClaimsCount)}
                        includeCompleteButton
                        includeDeleteButton
                      />
                    </div>
                  )}

                  {currentOtherClaims.slice(0, otherClaimsCount).length > 0 && (
                    <ClaimDetails
                      petDetails={petDetails}
                      claimDetails={currentOtherClaims.slice(0, otherClaimsCount)}
                    />
                  )}
                </>
              )}
            </div>
            {(currentSavedClaims.slice(0, savedClaimsCount).length > 0 ||
              currentOtherClaims.slice(0, otherClaimsCount).length > 0) && (
              <div className="myLatestClaimsFooter">
                <LatestClaimsActionButtons />
              </div>
            )}
          </main>
        </>
      )}
    </>
  );
};
